import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";

import { useApi } from "@/lib/api";
import CitedSitesPanel from "@/pages/admin/CitedSitesPanel";

/**
 * Global tracking view: one row per approved, non-internal creator with the
 * latest tracked-run named total, latest sighting result, and a link to the
 * creator's Tracked searches tab. Lightweight by design — the per-creator
 * monthly cron runs are already stored, so this is a read-only summary.
 */
export default function AdminTracking() {
    const { request } = useApi();
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const res = await request("/admin/creators");
            const creators = (res?.creators || []).filter(
                (c) => c.approved_at && !c.is_internal && !c.archived_at,
            );
            setRows(creators);
        } catch (err) {
            toast.error(err?.message || "Could not load tracking view.");
        } finally {
            setLoading(false);
        }
    }, [request]);

    useEffect(() => {
        void load();
    }, [load]);

    return (
        <section data-testid="admin-tracking-root">
            <header className="mb-5">
                <h1 className="font-display text-3xl font-bold tracking-tight">
                    Tracking
                </h1>
                <p className="text-sm text-black/60 mt-1">
                    Latest named status across every approved creator.
                </p>
            </header>
            <div
                data-testid="admin-tracking-list"
                className="bg-white border border-black/10 rounded-2xl divide-y divide-black/10"
            >
                {loading && (
                    <div className="p-4 text-sm text-black/50">Loading...</div>
                )}
                {!loading && rows.length === 0 && (
                    <div className="p-6 text-sm text-black/60">
                        No approved creators yet.
                    </div>
                )}
                {!loading &&
                    rows.map((c) => (
                        <Link
                            key={c.id}
                            to={`/admin/creators/${c.id}`}
                            data-testid={`admin-tracking-row-${c.id}`}
                            className="flex items-center justify-between gap-3 px-4 py-3 hover:bg-black/[0.03] no-underline text-ink"
                        >
                            <div className="min-w-0">
                                <div className="font-mono text-sm text-black/80">
                                    /@{c.samoony_handle || "no-handle"}
                                </div>
                                <div className="text-xs text-black/60">
                                    {c.display_name || c.first_name || ""}
                                </div>
                            </div>
                            <StatusPill status={c.name_status} />
                        </Link>
                    ))}
            </div>
            <CitedSitesPanel />
        </section>
    );
}

function StatusPill({ status }) {
    const map = {
        named: { label: "Named", cls: "bg-signal/15 text-signal" },
        not_yet_named: {
            label: "Not yet named",
            cls: "bg-black/5 text-black/60",
        },
        no_run_yet: {
            label: "No run yet",
            cls: "bg-black/5 text-black/50",
        },
    };
    const m = map[status] || map.no_run_yet;
    return (
        <span
            className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-mono uppercase tracking-wider ${m.cls}`}
        >
            {m.label}
        </span>
    );
}
